import { createSlice, PayloadAction } from "@reduxjs/toolkit";
import { AppThunk } from "app/store";
import { api } from "api/api";
import {
  RelatedParentMerchantDto,
  RelatedChildMerchantDto,
  MerchantDetailsDto,
} from "api/generated/models";
import { FeatureState } from "models/FeatureState";

interface State {
  relationState: FeatureState;
  parentRelations: RelatedParentMerchantDto[];
  childRelations: RelatedChildMerchantDto[];
  availableParents: MerchantDetailsDto[];
  isAddParentModalOpen: boolean;
}

const initialState: State = {
  relationState: FeatureState.Init,
  parentRelations: [],
  childRelations: [],
  availableParents: [],
  isAddParentModalOpen: false,
};

const slice = createSlice({
  name: "merchantRelations",
  initialState,
  reducers: {
    setRelationState(state, action: PayloadAction<FeatureState>) {
      state.relationState = action.payload;
    },
    setParentRelations(
      state,
      action: PayloadAction<RelatedParentMerchantDto[]>
    ) {
      state.parentRelations = action.payload;
    },
    setChildRelations(state, action: PayloadAction<RelatedChildMerchantDto[]>) {
      state.childRelations = action.payload;
    },
    setAvailableParents(state, action: PayloadAction<MerchantDetailsDto[]>) {
      state.availableParents = action.payload;
    },
    clearRelations(state) {
      state.relationState = FeatureState.Init;
      state.parentRelations = [];
      state.childRelations = [];
      state.availableParents = [];
    },
    openAddParentModal(state) {
      state.isAddParentModalOpen = true;
    },
    closeAddParentModal(state) {
      state.isAddParentModalOpen = false;
    },
  },
});

export const {
  setRelationState,
  setParentRelations,
  setChildRelations,
  setAvailableParents,
  clearRelations,
  openAddParentModal,
  closeAddParentModal,
} = slice.actions;

const getRelations =
  (merchantId: number): AppThunk =>
  async (dispatch) => {
    dispatch(setRelationState(FeatureState.Loading));
    try {
      const [parents, children] = await Promise.all([
        api.merchant.merchantGetParents(merchantId),
        api.merchant.merchantGetChildren(merchantId),
      ]);
      await dispatch(getAvailableParents(merchantId));
      dispatch(setParentRelations(parents.data));
      dispatch(setChildRelations(children.data));
      dispatch(setRelationState(FeatureState.Success));
    } catch {
      dispatch(setRelationState(FeatureState.Error));
    }
  };

const getAvailableParents =
  (merchantId: number): AppThunk =>
  async (dispatch) => {
    try {
      const res = await api.merchant.merchantGetAvailableParents(merchantId);
      dispatch(setAvailableParents(res.data));
    } catch (e) {
      console.error(e);
    }
  };

const addParentRelation =
  (parentMerchantId: number): AppThunk =>
  async (dispatch, getState) => {
    const childMerchantId = getState().merchant.merchantDetails?.id;
    if (!childMerchantId) return;

    dispatch(setRelationState(FeatureState.Loading));
    try {
      await api.merchant.merchantAddParent({
        parentMerchantId,
        childMerchantId,
      });
      await dispatch(getRelations(childMerchantId));
      dispatch(setRelationState(FeatureState.Success));
    } catch {
      dispatch(setRelationState(FeatureState.Error));
    }
  };

const deleteParentRelation =
  (relationId: number): AppThunk =>
  async (dispatch, getState) => {
    const merchantId = getState().merchant.merchantDetails?.id;
    if (!merchantId) return;

    try {
      await api.merchant.merchantDeleteParent(relationId);
      await dispatch(getRelations(merchantId));
    } catch (e) {
      console.error(e);
    }
  };

export const merchantRelationsActions = {
  getRelations,
  getAvailableParents,
  addParentRelation,
  deleteParentRelation,
  clearRelations,
  openAddParentModal,
  closeAddParentModal,
};

export const merchantRelationsReducer = slice.reducer;
